<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationPinsAPI from 'dashboard/api/conversationPins';
import types from 'dashboard/store/mutation-types';

const props = defineProps({ chat: { type: Object, required: true } });
const store = useStore();
const { t } = useI18n();
const saving = ref(false);
const label = computed(() => t(props.chat.pinned ? 'CONVERSATION.PIN.UNPIN' : 'CONVERSATION.PIN.PIN'));

const togglePin = async () => {
  if (saving.value) return;

  const accountId = store.getters.getCurrentAccountId;
  saving.value = true;
  try {
    const response = props.chat.pinned
      ? await ConversationPinsAPI.unpin(props.chat.id)
      : await ConversationPinsAPI.pin(props.chat.id);
    // A response from the previous account must not seed a partial conversation.
    if (store.getters.getCurrentAccountId !== accountId) return;
    if (store.getters.getConversationById(response.data.id)) {
      store.commit(types.UPDATE_CONVERSATION, response.data);
    }
    useAlert(t(response.data.pinned ? 'CONVERSATION.PIN.SAVED' : 'CONVERSATION.PIN.REMOVED'));
  } catch (error) {
    if (!error.isAxiosError) throw error;
    useAlert(t('CONVERSATION.PIN.ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <NextButton
    v-tooltip.bottom="label"
    :icon="chat.pinned ? 'i-lucide-pin-off' : 'i-lucide-pin'"
    :aria-label="label"
    :aria-pressed="!!chat.pinned"
    :is-loading="saving"
    :disabled="saving"
    :faded="chat.pinned"
    :ghost="!chat.pinned"
    class="min-h-10 min-w-10"
    @click="togglePin"
  />
</template>
